import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, ChevronDown, ClipboardCheck, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import AgreementsShell from "@/components/agreements/AgreementsShell";
import ChecklistRow from "@/components/agreements/ChecklistRow";
import CopyButton from "@/components/agreements/CopyButton";
import { AttorneyBadgeNote } from "@/components/agreements/LegalDisclaimer";
import {
  CHECKLIST_PHASES,
  CHECKLIST_STATUSES,
  EMPTY_CHECKLIST_ROW,
  phaseProgress,
  upcomingRenewals,
} from "@/data/agreements/masterChecklist";
import { DOC_BY_ID } from "@/data/agreements/documentLibrary";
import { STAGE_BY_ID } from "@/data/agreements/stages";
import { useAgreementProject } from "@/hooks/useAgreementProject";

/**
 * MasterChecklist — route: /agreements/checklist
 *
 * THE CPG AGREEMENT MASTER CHECKLIST. Eleven phases in the order a brand meets them, tracked
 * per document: status, counterparty, dates, attorney reviewed, signed copy stored.
 */

const STATUS_BY_ID = Object.fromEntries(CHECKLIST_STATUSES.map((s) => [s.id, s]));
const RENEWAL_WINDOW_DAYS = 90;

const PHASES = [...CHECKLIST_PHASES].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

export default function MasterChecklist() {
  const { intake, plan, checklist, setChecklistRow, progress, loading } = useAgreementProject();

  const [onlyPlan, setOnlyPlan] = useState(false);
  const [hideNotNeeded, setHideNotNeeded] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  const planLevels = useMemo(() => {
    const map = {};
    for (const item of plan?.items || []) map[item.docId] = item.level;
    return map;
  }, [plan]);
  const planCount = Object.keys(planLevels).length;

  const [open, setOpen] = useState(() => {
    const inStage = PHASES.filter((p) => p.stage === intake?.stage).map((p) => p.id);
    return new Set(inStage.length > 0 ? inStage : [PHASES[0]?.id].filter(Boolean));
  });
  const toggle = (id) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // The cached stage may differ from the saved one; once the backend answer lands, open the
  // phases for the founder's real stage. Runs once, so it never fights a manual toggle.
  const syncedToStage = useRef(false);
  useEffect(() => {
    if (loading || syncedToStage.current) return;
    syncedToStage.current = true;
    const inStage = PHASES.filter((p) => p.stage === intake?.stage).map((p) => p.id);
    if (inStage.length > 0) setOpen(new Set(inStage));
  }, [loading, intake?.stage]);

  const rowFor = (docId) => ({ ...EMPTY_CHECKLIST_ROW, ...(checklist?.[docId] || {}) });

  const visibleDocs = (phase) =>
    phase.docs.filter(([docId]) => {
      if (!DOC_BY_ID[docId]) return false;
      if (onlyPlan && !planLevels[docId]) return false;
      const row = rowFor(docId);
      if (hideNotNeeded && row.status === "not-needed") return false;
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      return true;
    });

  const renewals = useMemo(() => upcomingRenewals(checklist || {}, RENEWAL_WINDOW_DAYS), [checklist]);
  const checklistText = useMemo(() => buildChecklistText(checklist || {}, planLevels), [checklist, planLevels]);

  const filtersOn = onlyPlan || hideNotNeeded || statusFilter !== "all";

  return (
    <AgreementsShell
      wide
      crumbs={[{ label: "Master checklist" }]}
      title="The CPG Agreement Master Checklist"
      subtitle="Every agreement a packaged-goods brand meets, in the order it meets them. Track status, counterparty, dates and whether the signed copy is actually filed somewhere you can find it."
      actions={
        <>
          <CopyButton text={checklistText} label="Export checklist" variant="outline" />
          <Button asChild variant="outline">
            <Link to="/agreements/plan">My plan</Link>
          </Button>
        </>
      }
    >
      {loading && (
        <p className="mb-4 flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading your saved checklist…
        </p>
      )}

      {/* Overall progress */}
      <div className="mb-5 rounded-xl border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-600 text-white">
              <ClipboardCheck className="h-5 w-5" />
            </span>
            <div>
              <p className="font-semibold">
                {progress.done} of {progress.total} signed
                <span className="ml-2 text-sm font-normal text-muted-foreground">{progress.percent}%</span>
              </p>
              <p className="text-xs text-muted-foreground">
                Rows marked “not needed” drop out of the total, so the percentage reflects your brand, not a generic list.
              </p>
            </div>
          </div>
          <Progress value={progress.percent} className="h-2 w-full sm:w-56" />
        </div>
      </div>

      {/* Upcoming renewals */}
      {renewals.length > 0 && (
        <section className="mb-5 rounded-xl border border-pink-300 bg-pink-50 p-4">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-pink-900">
            <CalendarClock className="h-4 w-4 text-pink-600" />
            {renewals.length} agreement{renewals.length === 1 ? "" : "s"} expire or renew in the next {RENEWAL_WINDOW_DAYS} days
          </h2>
          <p className="mt-1 text-xs text-pink-900/90">
            Auto-renewal is how a distributor agreement you meant to renegotiate rolls into another full term. Check the
            notice window on each one now, not on the day it lapses.
          </p>
          <ul className="mt-3 space-y-1.5">
            {renewals.map((r) => (
              <li key={r.docId} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs text-pink-950">
                <Link to={`/agreements/library/${r.docId}`} className="font-medium underline underline-offset-2">
                  {DOC_BY_ID[r.docId]?.name || r.docId}
                </Link>
                {r.counterparty && <span className="text-pink-900/80">· {r.counterparty}</span>}
                <span className="text-pink-900/80">· {r.expirationDate}</span>
                <span className="font-semibold">
                  · {r.daysAway} day{r.daysAway === 1 ? "" : "s"} away
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Filters */}
      <div className="mb-5 flex flex-col gap-3 rounded-xl border bg-card p-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant={onlyPlan ? "default" : "outline"}
            onClick={() => setOnlyPlan((v) => !v)}
            disabled={planCount === 0}
            className={cn(onlyPlan && "bg-indigo-600 hover:bg-indigo-700")}
          >
            <Sparkles className="mr-1.5 h-3.5 w-3.5" /> Show only my plan
            {planCount > 0 && <span className="ml-1.5 opacity-70">({planCount})</span>}
          </Button>
          <Button
            type="button"
            size="sm"
            variant={hideNotNeeded ? "default" : "outline"}
            onClick={() => setHideNotNeeded((v) => !v)}
            className={cn(hideNotNeeded && "bg-indigo-600 hover:bg-indigo-700")}
          >
            Hide not-needed
          </Button>
          {filtersOn && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setOnlyPlan(false);
                setHideNotNeeded(false);
                setStatusFilter("all");
              }}
            >
              Clear filters
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <span className="mr-1 text-xs font-medium text-muted-foreground">Status</span>
          {[{ id: "all", label: "All" }, ...CHECKLIST_STATUSES].map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setStatusFilter(s.id)}
              aria-pressed={statusFilter === s.id}
              className={cn(
                "rounded-full border px-2.5 py-1 text-xs",
                statusFilter === s.id
                  ? "border-indigo-500 bg-indigo-600 text-white"
                  : "text-muted-foreground hover:bg-muted",
              )}
            >
              {s.label}
            </button>
          ))}
        </div>

        {planCount === 0 && (
          <p className="text-xs text-muted-foreground">
            <Link className="underline" to="/agreements/intake">
              Complete the intake
            </Link>{" "}
            to filter this list down to the agreements your brand actually needs.
          </p>
        )}
      </div>

      {/* Phases */}
      <div className="space-y-3">
        {PHASES.map((phase, index) => {
          const p = phaseProgress(phase, checklist || {});
          const docs = visibleDocs(phase);
          const isOpen = open.has(phase.id);
          const stage = STAGE_BY_ID[phase.stage];
          return (
            <Card key={phase.id} className="overflow-hidden">
              <button
                type="button"
                onClick={() => toggle(phase.id)}
                aria-expanded={isOpen}
                className="flex w-full items-start gap-3 p-4 text-left hover:bg-muted/50"
              >
                <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border bg-background text-xs font-semibold tabular-nums">
                  {index + 1}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{phase.title}</span>
                    {stage && (
                      <Badge variant="outline" className="font-normal">
                        {stage.name}
                      </Badge>
                    )}
                    {phase.attorneyLed && (
                      <Badge className="border-pink-300 bg-pink-100 font-normal text-pink-900 hover:bg-pink-100">
                        Attorney-led
                      </Badge>
                    )}
                  </span>
                  <span className="mt-1 block text-xs text-muted-foreground">{phase.blurb}</span>
                  <span className="mt-2 flex items-center gap-2">
                    <Progress value={p.percent} className="h-1.5 w-28" />
                    <span className="text-[11px] tabular-nums text-muted-foreground">
                      {p.done}/{p.total} signed
                    </span>
                  </span>
                </span>
                <ChevronDown
                  className={cn("mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform", isOpen && "rotate-180")}
                />
              </button>

              {isOpen && (
                <CardContent className="border-t bg-muted/20 p-3 sm:p-4">
                  {phase.attorneyLed && (
                    <AttorneyBadgeNote
                      level="strongly-recommended"
                      note="This whole phase is attorney-led. Use these rows to track what exists and what counsel still needs to produce — not as documents to draft yourself."
                      className="mb-3"
                    />
                  )}

                  {docs.length === 0 ? (
                    <p className="px-1 py-3 text-xs text-muted-foreground">
                      {filtersOn
                        ? "No rows in this phase match your filters."
                        : "No documents resolve for this phase."}
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {docs.map(([docId, defaultLevel]) => {
                        const doc = DOC_BY_ID[docId];
                        return (
                          <ChecklistRow
                            key={docId}
                            doc={doc}
                            row={rowFor(docId)}
                            level={planLevels[docId] || defaultLevel}
                            onChange={(patch) => setChecklistRow(docId, patch)}
                          />
                        );
                      })}
                    </div>
                  )}

                  {filtersOn && docs.length > 0 && docs.length < phase.docs.length && (
                    <p className="mt-3 px-1 text-[11px] text-muted-foreground">
                      {phase.docs.length - docs.length} row{phase.docs.length - docs.length === 1 ? "" : "s"} hidden by your
                      filters.
                    </p>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      <div className="mt-6">
        <AttorneyBadgeNote
          level="yes"
          note="A checklist tracks whether an agreement exists. It cannot tell you whether the one you signed is any good — that is what the entry for each document, and your attorney, are for."
        />
      </div>
    </AgreementsShell>
  );
}

/** Plain-text export: every phase, every row, with status, counterparty and dates. */
function buildChecklistText(rows, planLevels) {
  const lines = [];
  lines.push("THE CPG AGREEMENT MASTER CHECKLIST");
  lines.push("");
  lines.push(
    "Educational and informational only. Not legal advice. Have important agreements reviewed by a qualified attorney before signing.",
  );
  lines.push("");

  for (const phase of PHASES) {
    const p = phaseProgress(phase, rows);
    lines.push("─".repeat(70));
    lines.push(`${phase.order}. ${phase.title.toUpperCase()}  —  ${p.done}/${p.total} signed${phase.attorneyLed ? "  [attorney-led]" : ""}`);
    lines.push(`   ${phase.blurb}`);
    lines.push("");
    for (const [docId, defaultLevel] of phase.docs) {
      const doc = DOC_BY_ID[docId];
      if (!doc) continue;
      const row = { ...EMPTY_CHECKLIST_ROW, ...(rows[docId] || {}) };
      const status = STATUS_BY_ID[row.status]?.label || row.status;
      const level = planLevels[docId] || defaultLevel;
      lines.push(`   [${status}] ${doc.name}  (${level})`);
      const detail = [];
      if (row.counterparty) detail.push(`counterparty: ${row.counterparty}`);
      if (row.effectiveDate) detail.push(`effective: ${row.effectiveDate}`);
      if (row.expirationDate) detail.push(`expires: ${row.expirationDate}`);
      if (row.attorneyReviewed) detail.push("attorney reviewed");
      if (row.signedCopyStored) detail.push("signed copy stored");
      if (detail.length) lines.push(`       ${detail.join(" · ")}`);
      if (row.notes) lines.push(`       notes: ${row.notes}`);
    }
    lines.push("");
  }
  return lines.join("\n");
}
